import { afterAnswerWrites, invalidateAnswerCaches, progressCache } from '@/api/answerWrites';
import { writeOutbox } from '@/api/attempts';
import { type Lang, type List, request } from '@/api/client';

export interface TopicProgress {
  topic: number;
  seen: number;
  correct: number;
  mastery: number;
}

export interface ProgressCounters {
  bilets_passed: number;
  bilets_total: number;
  exams_submitted: number;
  perfect_exams: number;
  topics_mastered: number;
  bank_total: number;
}

export interface Progress {
  practiced_today: boolean;
  total_answered: number;
  distinct_questions: number;
  correct_rate: number;
  per_topic: TopicProgress[];
  counters: ProgressCounters;
}

export function getProgress(): Promise<Progress> {
  return progressCache.get('', () => afterAnswerWrites(() => request<Progress>('/me/progress')));
}

export interface ResetResult {
  deleted: number;
}

export function resetProgress(): Promise<ResetResult> {
  writeOutbox([]);
  return afterAnswerWrites(() => request<ResetResult>('/me/progress', { method: 'DELETE' })).then(
    (result) => {
      invalidateAnswerCaches();
      return result;
    },
  );
}

export interface Me {
  id: string;
  created_at: string | null;
  phone: string | null;
  telegram_id: number | null;
}

export function getMe(): Promise<Me> {
  return request<Me>('/users/me');
}

export function updateMe(body: { phone: null }): Promise<Me> {
  return request<Me>('/users/me', { method: 'PATCH', body: JSON.stringify(body) });
}

export function sharePhone(contactData: string): Promise<Me> {
  return request<Me>('/users/me/phone', {
    method: 'POST',
    body: JSON.stringify({ contact_data: contactData }),
  });
}

export interface UserSettings {
  auto_next_correct: boolean;
  auto_next_wrong: boolean;
  shuffle_questions: boolean;
  shuffle_answers: boolean;
  hide_explanation: boolean;
  font_size: string;
  language: Lang;
}

export function updateMySettings(body: Partial<UserSettings>): Promise<UserSettings> {
  return request<UserSettings>('/me/settings', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export interface ActivityEntry {
  question_id: string;
  original_id: number;
  question_text: string | null;
  topic: number;
  is_correct: boolean;
  mode: string;
  answered_at: string;
}

export function getActivity(lang: Lang, skip = 0, limit = 50): Promise<List<ActivityEntry>> {
  return afterAnswerWrites(() =>
    request<List<ActivityEntry>>(`/me/activity?lang=${lang}&skip=${skip}&limit=${limit}`),
  );
}

export function loginTelegram(initDataRaw: string): Promise<{ access_token: string }> {
  return request<{ access_token: string; progress?: Progress }>('/login/telegram', {
    method: 'POST',
    body: JSON.stringify({ init_data: initDataRaw }),
  }).then(({ access_token, progress }) => {
    if (progress) progressCache.put('', progress);
    return { access_token };
  });
}
